import moment from "moment";
import { GeoPictureValue, V2Question, V2Response } from "./helper";
import { QUESTION_TYPES } from "./constants";
import {
  mentorEmailError,
  isMentorPhoneInput,
  MENTOR_EMAIL_REQUIRED_MESSAGE,
  MENTOR_PHONE_MESSAGE,
  WEEK_ZERO_MENTOR_EMAIL_MARKER,
  WEEK_ZERO_MENTOR_PHONE_MARKER
} from "./weekZeroLocation";

/**
 * v2 form helpers (no UI):
 *  - `buildResponses`            antd form values → `{ questionId, answer }[]` submission payload
 *  - `buildFieldValuesFromDraft` saved responses → antd field values for pre-fill
 *  - `buildRules`                a question's validation → antd `Form.Item` rules
 */

type AnswerValue = string | number | string[] | null;

const DATE_FORMAT = "YYYY-MM-DD";
const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

// Normalise a single antd form value into the v2 answer for its question type.
const normaliseAnswer = (question: V2Question, value: unknown): AnswerValue => {
  if (value === undefined || value === null || value === "") return null;

  switch (question.questionType) {
    case QUESTION_TYPES.CHECKBOX:
    case QUESTION_TYPES.MULTISELECT:
      return Array.isArray(value) && value.length > 0 ? (value as string[]) : null;

    case QUESTION_TYPES.NUMBER:
    case QUESTION_TYPES.RATING:
      return typeof value === "number" ? value : Number(value);

    case QUESTION_TYPES.DATE_PICKER:
      return moment.isMoment(value) ? value.format(DATE_FORMAT) : (value as string);

    case QUESTION_TYPES.DATE_TIME_PICKER:
      return moment.isMoment(value) ? value.format(DATE_TIME_FORMAT) : (value as string);

    default:
      return value as string;
  }
};

// Build the v2 submission payload: one { questionId, answer } per question.
//
// GEO_PICTURE is the one type whose field value is an object rather than a scalar. It is split here
// so the stored answer stays a plain URL like every other upload-shaped question, with the capture's
// coordinates and time travelling alongside in `otherFields`.
export const buildResponses = (questions: V2Question[], values: Record<string, unknown>): V2Response[] =>
  (questions || []).map(question => {
    if (question.questionType === QUESTION_TYPES.GEO_PICTURE) {
      const geo = values?.[question.id] as GeoPictureValue | undefined;
      return {
        questionId: question.id,
        answer: geo?.url ?? null,
        // Only what the device reported. `place` is deliberately left out, even when a pre-filled value
        // carries one: core-api resolves it from these coordinates and discards any client-sent value.
        otherFields: geo
          ? {
              capture: {
                latitude: geo.latitude,
                longitude: geo.longitude,
                accuracy: geo.accuracy ?? null,
                altitude: geo.altitude ?? null,
                capturedAt: geo.capturedAt
              }
            }
          : null
      };
    }

    return {
      questionId: question.id,
      answer: normaliseAnswer(question, values?.[question.id])
    };
  });

// Convert a saved submission's responses back into antd field values for pre-fill.
export const buildFieldValuesFromDraft = (questions: V2Question[], responses: V2Response[] | null | undefined): Record<string, unknown> => {
  const byId = new Map((questions || []).map(q => [q.id, q]));
  const values: Record<string, unknown> = {};

  (responses || []).forEach(({ questionId, answer, otherFields }) => {
    if (answer === null || answer === undefined) return;
    const question = byId.get(questionId);
    if (!question) return;

    if (question.questionType === QUESTION_TYPES.GEO_PICTURE) {
      // Reassemble the object the input expects from the two columns it was split across. A response
      // with no `capture` still pre-fills the photo; the input renders no coordinate line for it.
      const capture = (otherFields?.capture ?? {}) as Partial<GeoPictureValue>;
      values[questionId] = {
        url: answer as string,
        latitude: capture.latitude,
        longitude: capture.longitude,
        accuracy: capture.accuracy,
        altitude: capture.altitude,
        capturedAt: capture.capturedAt,
        // Kept so a read-only view shows the stored place without looking it up again.
        place: capture.place
      };
    } else if (question.questionType === QUESTION_TYPES.DATE_PICKER) {
      values[questionId] = moment(answer as string, DATE_FORMAT);
    } else if (question.questionType === QUESTION_TYPES.DATE_TIME_PICKER) {
      values[questionId] = moment(answer as string, DATE_TIME_FORMAT);
    } else {
      values[questionId] = answer;
    }
  });

  return values;
};

/** Week 0 details questions whose answer must have a format core-api will copy, by `metadata.weekZeroField`. */
const WEEK_ZERO_FORMAT_CHECKS: Record<string, { error: (value: unknown) => string | undefined }> = {
  [WEEK_ZERO_MENTOR_EMAIL_MARKER]: { error: mentorEmailError },
  [WEEK_ZERO_MENTOR_PHONE_MARKER]: { error: value => (isMentorPhoneInput(value) ? undefined : MENTOR_PHONE_MESSAGE) }
};

// Map a question's validation rules to antd Form.Item rules (text length + required; the Week 0 mentor email and phone formats).
export const buildRules = (question: V2Question) => {
  const rules: Record<string, unknown>[] = [];
  const validation = question.validation || {};

  const marker = (question.metadata as { weekZeroField?: string } | null | undefined)?.weekZeroField;
  if (question.isRequired) {
    rules.push({
      required: true,
      message: marker === WEEK_ZERO_MENTOR_EMAIL_MARKER ? MENTOR_EMAIL_REQUIRED_MESSAGE : "This field is required",
      ...(marker === WEEK_ZERO_MENTOR_EMAIL_MARKER && { whitespace: true })
    });
  }
  if (typeof validation.minLength === "number") {
    rules.push({ min: validation.minLength, message: `Must be at least ${validation.minLength} characters` });
  }
  if (typeof validation.maxLength === "number") {
    rules.push({ max: validation.maxLength, message: `Must be at most ${validation.maxLength} characters` });
  }
  // Week 0 details: the builder sends no format for the external mentor email and phone, and core-api silently skips an
  // invalid one (the submit still succeeds), so the format is checked here before Submit. Found by marker, never by label.
  const formatCheck = marker ? WEEK_ZERO_FORMAT_CHECKS[marker] : undefined;
  if (formatCheck) {
    rules.push({
      validator: (_rule: unknown, value: unknown) => {
        const error = formatCheck.error(value);
        return error ? Promise.reject(new Error(error)) : Promise.resolve();
      }
    });
  }

  return rules;
};
